import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { 
  Users, Image, Plus, Save, Trash2, Edit2, X, 
  Upload, Loader2, Eye, EyeOff, GraduationCap, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import SortableList from '../../components/admin/SortableList';
import FlagIcon from '../../components/FlagIcon';

const API = process.env.REACT_APP_BACKEND_URL;

const DISCIPLINES = [
  { id: 'skateboard', label: 'Deskorolka' },
  { id: 'bmx', label: 'BMX' },
  { id: 'rolki', label: 'Rolki' },
  { id: 'hulajnoga', label: 'Hulajnoga' },
  { id: 'mtb', label: 'MTB' },
  { id: 'gokart', label: 'Gokart' }
];

const AdminClasses = () => {
  const { token } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('instructors');
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Instructors state
  const [instructors, setInstructors] = useState([]);
  const [editingInstructor, setEditingInstructor] = useState(null);
  const [instructorForm, setInstructorForm] = useState({ 
    name: '', 
    specialization: {}, 
    bio: {}, 
    image: '', 
    disciplines: [],
    isActive: true 
  });
  const [uploadingInstructorImage, setUploadingInstructorImage] = useState(false);
  
  // Gallery state
  const [gallery, setGallery] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [reorderSaving, setReorderSaving] = useState(false);
  const [reorderSaved, setReorderSaved] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [langRes, instructorsRes, galleryRes] = await Promise.all([
        // Use admin endpoint to get ALL languages including inactive
        axios.get(`${API}/api/admin/languages`, { headers: { Authorization: `Bearer ${token}` } })
          .catch(() => axios.get(`${API}/api/languages`)),
        axios.get(`${API}/api/classes/admin/instructors`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/classes/admin/gallery`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      // Include ALL languages (active and inactive) for admin editing
      setLanguages(langRes.data.map(l => ({ ...l, isActive: l.isActive !== false })));
      setInstructors(instructorsRes.data);
      setGallery(galleryRes.data);
    } catch (error) {
      console.error('Error fetching classes data:', error);
      toast.error('Błąd podczas pobierania danych');
    } finally {
      setLoading(false);
    }
  };

  // ============ INSTRUCTORS ============
  
  const handleInstructorImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploadingInstructorImage(true);
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await axios.post(`${API}/api/classes/admin/instructors/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setInstructorForm(prev => ({ ...prev, image: response.data.url }));
      toast.success('Zdjęcie przesłane');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Błąd podczas przesyłania zdjęcia');
    } finally {
      setUploadingInstructorImage(false);
      e.target.value = '';
    }
  };

  const handleSaveInstructor = async () => {
    try {
      setSaving(true);
      if (editingInstructor) {
        await axios.put(`${API}/api/classes/admin/instructors/${editingInstructor}`, instructorForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Instruktor zaktualizowany');
      } else {
        await axios.post(`${API}/api/classes/admin/instructors`, instructorForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Instruktor dodany');
      }
      setEditingInstructor(null);
      setInstructorForm({ name: '', specialization: {}, bio: {}, image: '', disciplines: [], isActive: true });
      fetchData();
    } catch (error) {
      console.error('Error saving instructor:', error);
      toast.error('Błąd podczas zapisywania');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteInstructor = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć tego instruktora?')) return;
    
    try {
      await axios.delete(`${API}/api/classes/admin/instructors/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Instruktor usunięty');
      fetchData();
    } catch (error) {
      toast.error('Błąd podczas usuwania');
    }
  };

  const handleEditInstructor = (instructor) => {
    setEditingInstructor(instructor._id);
    setInstructorForm({
      name: instructor.name || '',
      specialization: instructor.specialization || {},
      bio: instructor.bio || {},
      image: instructor.image || '',
      disciplines: instructor.disciplines || [],
      isActive: instructor.isActive !== false
    });
  };

  const toggleDiscipline = (disciplineId) => {
    setInstructorForm(prev => ({
      ...prev,
      disciplines: prev.disciplines.includes(disciplineId)
        ? prev.disciplines.filter(d => d !== disciplineId)
        : [...prev.disciplines, disciplineId]
    }));
  };

  const handleReorderInstructors = async (newOrder) => {
    setInstructors(newOrder);
    try {
      setReorderSaving(true);
      await axios.put(`${API}/api/classes/admin/instructors/reorder`, {
        orderedIds: newOrder.map(i => i._id)
      }, { headers: { Authorization: `Bearer ${token}` } });
      setReorderSaved(true);
      setTimeout(() => setReorderSaved(false), 2000);
    } catch (error) {
      console.error('Error reordering instructors:', error);
      toast.error('Błąd podczas zmiany kolejności');
      fetchData();
    } finally {
      setReorderSaving(false);
    }
  };

  // ============ GALLERY ============
  
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await axios.post(`${API}/api/classes/admin/gallery/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Zdjęcie dodane do galerii');
      fetchData();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Błąd podczas przesyłania zdjęcia');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleDeleteGalleryImage = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to zdjęcie?')) return;
    
    try {
      await axios.delete(`${API}/api/classes/admin/gallery/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Zdjęcie usunięte');
      fetchData();
    } catch (error) {
      toast.error('Błąd podczas usuwania');
    }
  };

  const toggleGalleryImageActive = async (image) => {
    try {
      await axios.put(`${API}/api/classes/admin/gallery/${image._id}`, {
        isActive: !image.isActive
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      toast.error('Błąd podczas aktualizacji');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Zajęcia</h1>
          <p className="text-gray-400">Zarządzaj instruktorami i galerią zajęć</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white/5">
          <TabsTrigger value="instructors" className="data-[state=active]:bg-primary">
            <Users className="w-4 h-4 mr-2" />
            Instruktorzy ({instructors.length})
          </TabsTrigger>
          <TabsTrigger value="gallery" className="data-[state=active]:bg-primary">
            <Image className="w-4 h-4 mr-2" />
            Galeria ({gallery.length})
          </TabsTrigger>
        </TabsList>

        {/* INSTRUCTORS TAB */}
        <TabsContent value="instructors" className="space-y-4">
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-lg">
                {editingInstructor ? 'Edytuj instruktora' : 'Dodaj nowego instruktora'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Photo */}
              <div>
                <Label>Zdjęcie instruktora</Label>
                <div className="flex items-center gap-4 mt-2">
                  {instructorForm.image && (
                    <img 
                      src={instructorForm.image.startsWith('/') ? `${API}${instructorForm.image}` : instructorForm.image}
                      alt="Instructor"
                      className="w-24 h-24 object-cover rounded-full"
                    />
                  )}
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleInstructorImageUpload}
                      disabled={uploadingInstructorImage}
                      className="hidden"
                      id="instructor-image-upload"
                    />
                    <label htmlFor="instructor-image-upload">
                      <Button asChild disabled={uploadingInstructorImage} variant="outline">
                        <span>
                          {uploadingInstructorImage ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          {instructorForm.image ? 'Zmień zdjęcie' : 'Dodaj zdjęcie'}
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div>
                <Label>Imię i nazwisko</Label>
                <Input
                  value={instructorForm.name}
                  onChange={(e) => setInstructorForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Jan Kowalski"
                  className="bg-white/5 border-white/10 mt-2"
                />
              </div>

              {/* Disciplines */}
              <div>
                <Label>Dyscypliny</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {DISCIPLINES.map(disc => (
                    <Button
                      key={disc.id}
                      type="button"
                      variant={instructorForm.disciplines.includes(disc.id) ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => toggleDiscipline(disc.id)}
                    >
                      {disc.label}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Specialization */}
              <div>
                <Label>Specjalizacja</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  {languages.map(lang => (
                    <div key={lang.code}>
                      <Label className="text-xs text-gray-400">
                        <FlagIcon code={lang.code} size={16} /> {lang.code.toUpperCase()}
                        {!lang.isActive && <span className="ml-1 px-1 py-0.5 rounded bg-gray-500/30 text-gray-500 text-[9px]">OFF</span>}
                      </Label>
                      <Input
                        value={instructorForm.specialization[lang.code] || ''}
                        onChange={(e) => setInstructorForm(prev => ({
                          ...prev,
                          specialization: { ...prev.specialization, [lang.code]: e.target.value }
                        }))}
                        placeholder={`Specjalizacja (${lang.code})`}
                        className="bg-white/5 border-white/10"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Bio */}
              <div>
                <Label>Bio / Opis</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                  {languages.map(lang => (
                    <div key={lang.code}>
                      <Label className="text-xs text-gray-400">
                        <FlagIcon code={lang.code} size={16} /> {lang.code.toUpperCase()}
                        {!lang.isActive && <span className="ml-1 px-1 py-0.5 rounded bg-gray-500/30 text-gray-500 text-[9px]">OFF</span>}
                      </Label>
                      <Textarea
                        value={instructorForm.bio[lang.code] || ''}
                        onChange={(e) => setInstructorForm(prev => ({
                          ...prev,
                          bio: { ...prev.bio, [lang.code]: e.target.value }
                        }))}
                        placeholder={`Bio (${lang.code})`}
                        className="bg-white/5 border-white/10"
                        rows={3}
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Active toggle */}
              <div className="flex items-center gap-2">
                <Switch
                  checked={instructorForm.isActive}
                  onCheckedChange={(checked) => setInstructorForm(prev => ({ ...prev, isActive: checked }))}
                />
                <Label>Aktywny</Label>
              </div>
              
              <div className="flex gap-2">
                <Button onClick={handleSaveInstructor} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {editingInstructor ? 'Zapisz zmiany' : 'Dodaj instruktora'}
                </Button>
                {editingInstructor && (
                  <Button variant="outline" onClick={() => {
                    setEditingInstructor(null);
                    setInstructorForm({ name: '', specialization: {}, bio: {}, image: '', disciplines: [], isActive: true });
                  }}>
                    <X className="w-4 h-4 mr-2" />
                    Anuluj
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Instructors list - drag to reorder */}
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-400">Przeciągnij, aby zmienić kolejność wyświetlania</p>
            {reorderSaved && (
              <span className="flex items-center gap-1 text-green-400 text-sm">
                <CheckCircle className="w-4 h-4" /> Kolejność zapisana
              </span>
            )}
            {reorderSaving && (
              <span className="flex items-center gap-1 text-gray-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Zapisuję...
              </span>
            )}
          </div>
          <SortableList
            items={instructors}
            onReorder={handleReorderInstructors}
            idPrefix="instructor"
            renderItem={(instructor) => (
              <Card className={`bg-white/5 border-white/10 ${!instructor.isActive ? 'opacity-50' : ''}`}>
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    {instructor.image ? (
                      <img 
                        src={instructor.image.startsWith('/') ? `${API}${instructor.image}` : instructor.image}
                        alt={instructor.name}
                        className="w-16 h-16 object-cover rounded-full"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                        <Users className="w-8 h-8 text-primary" />
                      </div>
                    )}
                    
                    <div className="flex-1">
                      <h3 className="font-medium">{instructor.name || 'Bez nazwy'}</h3>
                      <p className="text-sm text-gray-400">{instructor.specialization?.pl}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {instructor.disciplines?.map(d => (
                          <span key={d} className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                            {DISCIPLINES.find(disc => disc.id === d)?.label || d}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEditInstructor(instructor)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteInstructor(instructor._id)}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          />
          
          {instructors.length === 0 && (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="py-8 text-center text-gray-400">
                Brak instruktorów. Dodaj pierwszego instruktora powyżej.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* GALLERY TAB */}
        <TabsContent value="gallery" className="space-y-4">
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  className="hidden"
                  id="gallery-upload"
                />
                <label htmlFor="gallery-upload">
                  <Button asChild disabled={uploadingImage}>
                    <span>
                      {uploadingImage ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Dodaj zdjęcie
                    </span>
                  </Button>
                </label>
                <p className="text-sm text-gray-400">
                  Obsługiwane formaty: JPG, PNG, GIF, WebP. Maksymalny rozmiar: 10MB
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Gallery grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {gallery.map(image => (
              <div key={image._id} className="relative group">
                <img
                  src={image.url.startsWith('/') ? `${API}${image.url}` : image.url}
                  alt={image.alt?.pl || 'Gallery image'}
                  className={`w-full h-32 object-cover rounded-lg ${!image.isActive ? 'opacity-50' : ''}`}
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleGalleryImageActive(image)}
                    className="text-white hover:bg-white/20"
                  >
                    {image.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteGalleryImage(image._id)}
                    className="text-red-400 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          
          {gallery.length === 0 && (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="py-8 text-center text-gray-400">
                Galeria jest pusta. Dodaj pierwsze zdjęcie powyżej.
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminClasses;
